import React, { useState, useEffect, Fragment } from "react";
import Breadcrumb from "../../components/common/breadcrumb";
import axiosConfig from "../../services/axiosConfig";
import setHeader from "../../components/Functions/setHeader";
import SweetAlert from 'sweetalert2';
import { Link } from "react-router-dom";
import NoDataHandler from "../../components/common/NoDataHandler";
import LoadingComponent from "../../components/common/LoadingComponent";
import {  formatDate } from "../../utils/dateFormatter";

const PurchaseList = () => {
    const today = new Date().toISOString().split("T")[0]; // Format: YYYY-MM-DD
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [suppliers, setSuppliers] = useState([]);
    const [filters, setFilters] = useState({
        supplier: '',
        from_date: today,
        to_date: today
    });

    useEffect(() => {
        getData();
        getStores();
    }, []);

    const getStores = () => {
        // Get unique stores from data or make a separate API call
        axiosConfig.get("all-suppliers", setHeader()) // Assuming you have this endpoint
            .then((res) => {
                setSuppliers(res.data.data);
            })
            .catch((error) => {
                console.error("Error fetching stores:", error);
                // If no stores endpoint, we'll extract from purchase data
            });
    };

    const getData = () => {
        setLoading(true);
        setData([]); // Reset data before fetching new data

        // Build JSON payload
        const payload = {
            supplier: filters.supplier || '',
            from_date: filters.from_date || '',
            to_date: filters.to_date || ''
        };

        axiosConfig.post("all-purchases", payload, setHeader())
            .then((res) => {
                setData(res.data.data);

                // If stores not loaded separately, extract from purchase data
                if (suppliers.length === 0) {
                    const uniqueStores = res.data.data.reduce((acc, item) => {
                        const existingStore = acc.find(store => store.storeId === item.store_id);
                        if (!existingStore) {
                            acc.push({
                                id: item.storeId,
                                name: item.store_name
                            });
                        }
                        return acc;
                    }, []);
                    setSuppliers(uniqueStores);
                }
            })
            .catch((error) => {
                console.error("Error fetching data:", error);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({
            ...prev,
            [field]: value
        }));

    };

    const handleFilter = () => {
        getData();
    };

    const handleClearFilters = () => {
        setFilters({
            store_id: '',
            from_date: '',
            to_date: ''
        });
        // Reset filters and reload data
        setTimeout(() => {
            getData();
        }, 100);
    };

    const DeleteData = (id) => {
        SweetAlert.fire({
            title: 'Are you sure?',
            text: "Once deleted, you will not be able to recover this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Ok',
            cancelButtonText: 'Cancel',
            reverseButtons: true,
        }).then((result) => {
            if (result.isConfirmed) {
                axiosConfig.get(`delete-purchase/${id}`, setHeader())
                    .then((res) => {
                        if (!res.data.error) {
                            SweetAlert.fire({
                                title: "Success",
                                text: res.data.messages,
                                icon: "success",
                            });
                            getData();
                        } else {
                            SweetAlert.fire({
                                title: "Oops!",
                                text: res.data.messages,
                                icon: "error",
                            });
                        }
                    })
                    .catch((error) => {
                        console.error("Error deleting data:", error);
                        SweetAlert.fire({
                            title: "Error",
                            text: "An error occurred while deleting the data.",
                            icon: "error",
                        });
                    });
            }
        });
    };

    if (loading) {
        return (
            <LoadingComponent />
        );
    }

    return (
        <Fragment>
            <Breadcrumb title="Purchase List" parent="Purchase" />
            <div className="container-fluid">
                <div className="row">
                    {/* Filter Section */}
                    <div className="col-sm-12">
                        <div className="card">
                            <div className="card-header p-3">
                                <h5>Filter Purchases</h5>
                            </div>
                            <div className="card-body p-3">
                                <div className="row">
                                    <div className="col-md-3">
                                        <div className="form-group">
                                            <label>Supplier</label>
                                            <select
                                                className="form-control"
                                                value={filters.store_id}
                                                onChange={(e) => handleFilterChange('store_id', e.target.value)}
                                            >
                                                <option value="">All Suppliers</option>
                                                {suppliers?.map((supplier) => (
                                                    <option key={supplier?.supplier} value={supplier?.supplier}>
                                                        {supplier?.supplier}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                    <div className="col-md-3">
                                        <div className="form-group">
                                            <label>From Date</label>
                                            <input
                                                type="date"
                                                className="form-control"
                                                value={filters.from_date}
                                                onChange={(e) => handleFilterChange('from_date', e.target.value)}
                                            />
                                        </div>
                                    </div>
                                    <div className="col-md-3">
                                        <div className="form-group">
                                            <label>To Date</label>
                                            <input
                                                type="date"
                                                className="form-control"
                                                value={filters.to_date}
                                                onChange={(e) => handleFilterChange('to_date', e.target.value)}
                                            />
                                        </div>
                                    </div>
                                    <div className="col-md-3">
                                        <div className="form-group">
                                            <label>&nbsp;</label>
                                            <div className="d-flex">
                                                <button
                                                    className="btn btn-primary me-2"
                                                    onClick={handleFilter}
                                                >
                                                    Filter
                                                </button>
                                                <button
                                                    className="btn btn-secondary"
                                                    onClick={handleClearFilters}
                                                >
                                                    Clear
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Data Table */}
                    <div className="col-sm-12">
                        <div className="card">
                            <div className="card-header p-3">
                                <h5>Purchase Records {data?.length > 0 ? (<>({data?.length})</>) : (<></>)}</h5>
                            </div>
                            <div className="card-body p-3">
                                {data?.length === 0 ? (
                                    <NoDataHandler title='No Purchases Found' />
                                ) : (
                                    <div className="table-responsive">
                                        <table className="table">
                                            <thead>
                                                <tr>
                                                    <th scope="col">{"#"}</th>
                                                    <th scope="col">{"Supplier Name"}</th>
                                                    <th scope="col">{"Purchase Date"}</th>
                                                    <th scope="col">{"Description"}</th>
                                                    <th scope="col">{"Action"}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {data?.map((item, index) => {
                                                    return (
                                                        <tr key={item.purchase_id}>
                                                            <th scope="row">{index + 1}</th>
                                                            <td>{item.supplier}</td>
                                                            <td>{formatDate(item.purchase_date)}</td>
                                                            {/* <td>{item.purchase_date}</td> */}
                                                            <td>{item.description}</td>
                                                            <td>
                                                                <Link
                                                                    to={`/pages/purchase-details/${item.purchase_id}`}
                                                                    className="btn btn-xs btn-outline-success me-2"
                                                                >
                                                                    View
                                                                </Link>
                                                                <Link
                                                                    to={`/pages/purchase-add?id=${item.purchase_id}`}
                                                                    className="btn btn-xs btn-outline-success me-2"
                                                                >
                                                                    Edit
                                                                </Link>
                                                                <button
                                                                    title="Delete"
                                                                    className="btn btn-xs btn-outline-danger"
                                                                    onClick={(e) => DeleteData(item.purchase_id)}
                                                                >
                                                                    Delete
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Fragment>
    );
};

export default PurchaseList;
